'use client';

import {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import type { SignatureRequestsProviderProps } from './SignatureRequestsProvider.types';
import { SignatureRequest } from '@/_types/models/signatureRequests';
import { UserContext } from '@/_contexts/UserProvider';
import { ClientDashboardContext } from '@/_contexts/client/ClientDashboardProvider';
import { getAllSignatureRequests } from '@/_api/admin-signature-requests';
import { UploadSignatureRequestDialog } from './components/UploadSignatureRequestDialog';

const SignatureRequestsContext = createContext<SignatureRequestsProviderProps>({
  filteredSignatureRequests: [],
  getSignatureRequests: () => Promise.resolve(),
  isLoading: false,
  isSending: false,
  selectedSignatureRequest: undefined,
  setSelectedSignatureRequest: () => {},
  setSending: () => null,
  signatureRequests: [],
});

const SignatureRequestsProvider = (props: any) => {
  const { token, isAdvisor } = useContext(UserContext);
  const { yearFilter } = useContext(ClientDashboardContext);

  const [signatureRequests, setSignatureRequests] = useState<
    Array<SignatureRequest>
  >([]);
  const [isSending, setSending] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);

  const filteredSignatureRequests: Array<SignatureRequest> = useMemo(() => {
    return signatureRequests
      ? signatureRequests?.filter?.((tr) => tr.tax_year === Number(yearFilter))
      : [];
  }, [yearFilter, signatureRequests]);

  const [selectedSignatureRequest, setSelectedSignatureRequest] = useState<
    SignatureRequest | undefined
  >(undefined);

  const _getAllSignatureRequests = useCallback(async () => {
    if (!token || !isAdvisor) return;

    try {
      setLoading(true);
      const signatureRequests = (await getAllSignatureRequests({
        token,
      })) as Array<SignatureRequest>;

      Array.isArray(signatureRequests) &&
        setSignatureRequests(signatureRequests);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isAdvisor, token]);

  useEffect(() => {
    _getAllSignatureRequests();
  }, [_getAllSignatureRequests]);

  return (
    <SignatureRequestsContext
      value={{
        filteredSignatureRequests,
        signatureRequests,
        isLoading,
        isSending,
        setSending,
        selectedSignatureRequest,
        setSelectedSignatureRequest,
        getSignatureRequests: _getAllSignatureRequests,
      }}
    >
      <UploadSignatureRequestDialog />
      {props.children}
    </SignatureRequestsContext>
  );
};

export { SignatureRequestsContext, SignatureRequestsProvider };
